import 'swiper/css';

import React, { Children, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { Swiper as SwiperType } from 'swiper';
import { Autoplay, FreeMode, Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';
import { IS_PRERENDER } from '@namespace/helpers';
import { Icon, IconSize } from '@namespace/icons2';
import { Box } from '../Box';
import { Dots } from '../Dots';

import styles from './index.module.css';

interface ArrowConfig {
  name: string;
  leftClass: string;
  rightClass: string;
  background?: string;
  size?: IconSize;
  isHideAtEdge?: boolean;
  moveCount?: number;
}

interface NavigationConfig {
  arrows?: boolean;
  dots?: Record<string, unknown>;
}

interface CarouselProps {
  children?: React.ReactNode;
  headerComponent?: React.ReactElement;
  rotationInterval?: number;
  pauseRotationOnFocus?: boolean;
  navigation?: NavigationConfig;
  animationDuration?: number;
  circular?: boolean;
  autoPlay?: boolean;
  arrow?: ArrowConfig;
  setIsMove?: (isMove: boolean) => void;
  classNames?: {
    container?: string;
    slideWrapper?: string;
  };
  selectedPanelIndex?: number;
  dotsWrapper?: string;
  slidesPerView?: number | 'auto';
  spaceBetween?: number;
  style?: React.CSSProperties | undefined;
  breakpoints?: Record<number | string, SwiperOptions>;
  className?: string;
  slideZIndexDecrease?: number;
  allowTouchMove?: boolean;
  setVisiblePanels?: (count: number) => void;
}

export const SwiperCarousel: React.FC<CarouselProps> = ({
  children,
  headerComponent,
  rotationInterval = 5,
  pauseRotationOnFocus = false,
  navigation = {},
  arrow = {
    name: 'icons/general/nav/back',
    leftClass: `${styles.controllerItem} ${styles.controllerArrow} ${styles.arrowPrevious}`,
    rightClass: `${styles.controllerItem} ${styles.controllerArrow} ${styles.arrowNext}`,
    background: `${styles.background}`,
    size: 'l',
    isHideAtEdge: false
  },
  setIsMove = () => {},
  classNames = {},
  selectedPanelIndex,
  dotsWrapper = '',
  slidesPerView = 1,
  spaceBetween = 0,
  animationDuration = 750,
  circular = false,
  autoPlay = true,
  style,
  breakpoints,
  slideZIndexDecrease,
  allowTouchMove = true,
  setVisiblePanels,
  className,
  ...props
}) => {
  const { arrows = true, dots = {} } = navigation;
  const swiperRef = React.useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isEnd, setIsEnd] = useState<boolean>(false);
  const [isBeginning, setIsBeginning] = useState<boolean>(true);
  const [visibleSlides, setVisibleSlides] = useState<number>(0);

  const animationStart = useCallback(() => {
    setIsMove(true);
  }, [setIsMove]);

  const animationStop = useCallback(() => {
    setIsMove(false);
  }, [setIsMove]);

  const childrenCount = Children.count(children);
  const showOneSlide =
    (slidesPerView === 1 || slidesPerView === 'auto') &&
    childrenCount === 1 &&
    Object.values(breakpoints || {}).every(
      (breakpoint) =>
        !breakpoint?.slidesPerView || breakpoint?.slidesPerView === 1
    );

  useEffect(() => {
    if (swiperRef) {
      onReach(swiperRef.current);
    }
  }, [childrenCount]);

  const onReach = (swiper: SwiperType | null) => {
    if (swiper) {
      setTimeout(() => {
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
      }, 100); // Timeout is required because isBeginning and isEnd are updated with a delay
    }
  };

  const onSlideChange = (swiper: SwiperType) => {
    setActiveIndex(swiper.realIndex);
    onReach(swiper);
  };

  const onInit = (swiper: SwiperType) => {
    if (circular || setVisiblePanels) {
      const updateVisibleSlides = () => {
        const visibleCount = swiper.slides.filter((slide) => {
          const slideLeft = slide.offsetLeft;
          const slideRight = slideLeft + slide.offsetWidth;
          const swiperLeft = swiper.translate * -1; // Swiper's left position in container
          const swiperRight = swiperLeft + swiper.width;

          return slideLeft >= swiperLeft && slideRight <= swiperRight;
        }).length;
        if (circular) {
          setVisibleSlides(visibleCount);
        }
        if (setVisiblePanels) {
          setVisiblePanels(visibleCount);
        }
      };

      updateVisibleSlides();
      swiper.on('slideChange', updateVisibleSlides);
      swiper.on('resize', updateVisibleSlides);
    }

    setActiveIndex(swiper.realIndex);
    onReach(swiper);
  };

  const handleAutoplay = (swiper: SwiperType) => {
    setActiveIndex(swiper.realIndex);
    onReach(swiper);
  };

  const onClickArrowRight = () => {
    if (swiperRef) {
      if (arrow.moveCount) {
        swiperRef.current?.slideToLoop(activeIndex + arrow.moveCount);
      } else {
        swiperRef.current?.slideNext();
      }
      onReach(swiperRef.current);
    }
  };
  const onClickArrowLeft = () => {
    if (swiperRef) {
      if (arrow.moveCount) {
        swiperRef.current?.slideToLoop(activeIndex - arrow.moveCount);
      } else {
        swiperRef.current?.slidePrev();
      }

      onReach(swiperRef.current);
    }
  };

  const slidesPerViewAuto = slidesPerView === 'auto';

  return (
    <Box className={clsx([styles.sliderContainer, classNames.container])}>
      {headerComponent &&
        React.cloneElement(headerComponent, {
          onClickArrowRight,
          onClickArrowLeft,
          arrowActiveStatus: {
            isRightArrowHidden: isEnd,
            isLeftArrowHidden: isBeginning
          }
        })}
      {!showOneSlide &&
        ((arrows && !arrow.isHideAtEdge) ||
          (arrows && arrow.isHideAtEdge && !isBeginning)) && (
          <Box onClick={onClickArrowLeft} className={arrow.leftClass}>
            <Box className={arrow.background} />
            <Icon className={styles.icon} name={arrow.name} size={arrow.size} />
          </Box>
        )}
      {!showOneSlide && (
        <Swiper
          style={style}
          modules={[
            ...(autoPlay && !IS_PRERENDER ? [Autoplay] : []),
            ...(allowTouchMove ? [Mousewheel, FreeMode] : [])
          ]}
          autoplay={
            rotationInterval
              ? {
                  delay: rotationInterval * 1000,
                  pauseOnMouseEnter: pauseRotationOnFocus
                }
              : false
          }
          loop={
            circular && childrenCount <= visibleSlides + 1 ? false : circular
          }
          speed={animationDuration}
          initialSlide={selectedPanelIndex ?? 0}
          className={clsx([styles.slider, className])}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            onInit(swiper);
          }}
          slidesPerView={slidesPerView}
          spaceBetween={spaceBetween}
          onSlideChange={onSlideChange}
          onInit={onInit}
          onAutoplay={handleAutoplay}
          breakpoints={breakpoints}
          freeMode={
            allowTouchMove
              ? {
                  enabled: true,
                  sticky: true
                }
              : undefined
          }
          mousewheel={
            allowTouchMove ? { enabled: true, forceToAxis: true } : undefined
          }
          allowTouchMove={allowTouchMove}
          touchRatio={0.5} // Decreasing swipe sensitivity
          onTransitionStart={animationStart}
          onTransitionEnd={animationStop}
          onTouchStart={animationStart}
          onTouchEnd={animationStop}
          {...props}
        >
          {React.Children.map(children, (child, index) => (
            <SwiperSlide
              {...(slideZIndexDecrease
                ? { style: { zIndex: slideZIndexDecrease - index } } // Need for UA13 TopGames
                : {})}
              key={`swiper-slide-${index + 1}`}
              className={clsx([
                slidesPerViewAuto &&
                  !classNames.slideWrapper &&
                  styles.slidesPerViewAuto,
                styles.slide,
                classNames.slideWrapper
              ])}
            >
              {child}
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      {showOneSlide && (
        <Box className={clsx([styles.slider, className])} style={style}>
          {React.Children.toArray(children)[0]}
        </Box>
      )}
      {dots && !isEmpty(dots) && (
        <Dots
          dotsNumber={childrenCount}
          activeIndex={activeIndex}
          setActiveIndex={(index) => {
            swiperRef.current?.slideToLoop(index);
            onReach(swiperRef.current);
          }}
          className={dotsWrapper}
          config={dots}
        />
      )}
      {!showOneSlide &&
        ((arrows && !arrow.isHideAtEdge) ||
          (arrows && arrow.isHideAtEdge && !isEnd)) && (
          <Box onClick={onClickArrowRight} className={arrow.rightClass}>
            <Box className={arrow.background} />
            <Icon className={styles.icon} name={arrow.name} size={arrow.size} />
          </Box>
        )}
    </Box>
  );
};
